const util = require('util');

const ERRORS = {
  'parameter not function': 'Parameter must be a function, was a %s.',
  'size less than 1': 'RingBuffer cannot be created with a size less than 1 (was %n).',
  'size not integer': 'RingBuffer cannot be created with a non integer size (was %s).',
};

/**
 * A ring buffer stores a recent window of a sequence of items.
 * @classdesc
 */
class RingBuffer {
  /**
   * Creates a RingBuffer, which allows a maximum number of items to be stored.
   *
   * @param {Number} size The maximum size of this buffer. This must be an integer larger than 0.
   * @constructor
   */
  constructor(size) {
    checkSize(size);
    this.maxSize = size;
    this.clear();
  }

  /**
   * Clears all items from this RingBuffer and resets it.
   * @return {undefined}
   */
  clear() {
    this.buffer = new Array(this.maxSize);
    this.next = 0;
    this.isFull = false;
  }

  /**
   * @return {*} the item most recently added into this RingBuffer,
   * or null if no items have been added.
   */
  newest() {
    const index = (this.next + this.maxSize - 1) % this.maxSize;
    let newest = null;
    if (this.isFull || index < this.next) {
      newest = this.buffer[index];
    }
    return newest;
  }

  /**
   * @return {*} the oldest item that is still in this RingBuffer,
   *              or null if no items have been added.
   */
  oldest() {
    let oldest = null;
    if (this.isFull) {
      oldest = this.buffer[this.next];
    } else if (this.next > 0) {
      oldest = this.buffer[0];
    }
    return oldest;
  }

  /**
   * @param {Number} n the index of the item to be returned.
   * @return {*} the nth oldest item that is stored or undefined
   *          if the index is larger than the number of items stored.
   */
  get(n) {
    if (n >= this.maxSize) {
      return undefined;
    }

    if (this.isFull) {
      return this.buffer[(this.next + n) % this.maxSize];
    }
    return this.buffer[n];
  }

  /**
   * Adds an item into the end of this RingBuffer, possibly pushing an item
   * out of the buffer in the process.
   *
   * @param {*} object an item to add into this buffer.
   * @return {*} the item that was pushed out of the buffer or null if the buffer
   *            is not full. Note, this is different to what an array.push returns.
   *
   */
  push(object) {
    let ousted = null;
    if (this.isFull) {
      ousted = this.oldest();
    }

    changeWindow(this, object);

    return ousted;
  }

  /**
   * Changes the size of a RingBuffer.
   *
   * This operation should not be expected to be performant; do not do it often.
   *
   * If the new size is smaller than the number of items in this window,
   * this operation may cause some objects to be pushed out of the window.
   *
   * @param {number} newSize the new size the window should take up. Must be a positive integer.
   * @return {undefined}
   */
  setSize(newSize) {
    checkSize(newSize);

    if (this.maxSize === newSize) {
      return;
    }
    const tmpBuffer = new RingBuffer(newSize);
    this.forEach(tmpBuffer.push.bind(tmpBuffer));

    this.maxSize = tmpBuffer.maxSize;
    this.buffer = tmpBuffer.buffer;
    this.next = tmpBuffer.next;
    this.isFull = tmpBuffer.isFull;
  }

  /**
   * Iterates over each of the items in this buffer from oldest to newest.
   *
   * @param {Function} func a function that will be called with each item.
   * @return {undefined}
   */
  forEach(func) {
    if (typeof func !== 'function') {
      throw new TypeError(errorMessage('parameter not function', typeof func));
    }

    for (let i = 0, end = this.getSize(); i < end; ++i) {
      const bufferIndex = this.isFull ? (this.next + i) % this.maxSize : i;
      func(this.buffer[bufferIndex]);
    }
  }

  /**
   * @returns {number} the number of items in this buffer.
   */
  getSize() {
    return this.isFull ? this.maxSize : this.next;
  }

  /**
   * @return {string} Returns a string representation of this RingBuffer.
   *          This is intended to be human readable for debugging
   *          - the exact format may change.
   */
  toString() {
    const result = ['{sidingwindow start='];
    result.push(this.next);
    result.push(' values=[');
    result.push(this.buffer.join(','));
    result.push('] }');

    return result.join('');
  }
}

function errorMessage(...args) {
  args[0] = ERRORS[args[0]];
  return util.format(...args);
}
// exposed for test purposes
RingBuffer.errorMessage = errorMessage;

function checkSize(size) {
  if (size !== (size | 0)) {
    throw new Error(errorMessage('size not integer', size));
  }
  if (size < 1) {
    throw new Error(errorMessage('size less than 1', size));
  }
}

function changeWindow(ringBuffer, incoming) {
  /* eslint no-param-reassign:0 */
  ringBuffer.buffer[ringBuffer.next] = incoming;
  ringBuffer.next = (ringBuffer.next + 1) % ringBuffer.maxSize;
  if (ringBuffer.next === 0) {
    ringBuffer.isFull = true;
  }
}

module.exports = RingBuffer;
